/**
 * Medical On Demand (MOD) Constants
 *
 * Hardcoded product data and question types for the Treatment Support feature.
 * This data is static - stored in frontend to avoid schema changes.
 */

import { env } from '@/util/env';

/**
 * Canonical user-facing label for the Medical On Demand feature surface
 * (badges, headings, CTAs, toasts). All UI copy should compose strings from
 * this constant rather than hardcoding "Treatment Support" inline.
 */
export const TREATMENT_SUPPORT_LABEL = 'Treatment Support';

/**
 * Backend-stored classification value for MOD (Treatment Support) experts.
 * This is the raw `specialty` field value used for expert detection — kept
 * separate from the display label so a copy change to `TREATMENT_SUPPORT_LABEL`
 * can never silently break the MOD expert predicate.
 */
export const MOD_SPECIALTY_VALUE = 'treatment support';

/**
 * Pre-Direct-to-MSL J&J Medical team name. Used when the `MOD_DIRECT_TO_MSL`
 * flag is OFF so the team keeps its legacy name in headers, sidebars,
 * inbox cards, and chat headers for users not in the rollout.
 */
export const J_J_MEDICAL_NAME_LEGACY = 'J&J Medical Information';

/**
 * Pre-Direct-to-MSL J&J Medical sidebar `about` paragraphs. Used when the
 * `MOD_DIRECT_TO_MSL` flag is OFF — keeps the original three-paragraph
 * copy (no MSL paragraph, original disclaimer wording) for users not in
 * the rollout.
 */
export const J_J_MEDICAL_ABOUT_LEGACY: string[] = [
  'The Johnson & Johnson (J&J) Medical Information Team consists of clinically trained healthcare professionals who specialize in delivering comprehensive scientific information to support the use of J&J Innovative Medicine products across all therapeutic areas.',
  'This team engages with healthcare providers and patients to address inquiries on product-related topics including dosing and administration, adverse event management, clinical trial data, and other areas of patient care. While the J&J Medical Information Team cannot provide individuals with treatment recommendations, their goal is to provide clinicians and patients with access to clinical and scientific information.',
  'Responses from J&J Medical Information are provided based on your unsolicited request and are not intended as an endorsement of any usage not contained in the prescribing information. For comprehensive product information, please refer to the full Prescribing Information. To report a possible adverse event or product quality complaint, please call J&J Medical Information immediately, at 1-800-526-7736.',
];

/**
 * The J&J Medical `about` paragraph describing the MIL team. Named so
 * `useModTeamCopy` can drop it while `MOD_MIL_ROUTE` is off: without the
 * MIL checkbox, a paragraph introducing MIL describes a team the reader
 * cannot send to.
 */
export const J_J_MEDICAL_ABOUT_MIL =
  'The Medical Implementation Lead (MIL) team specializes in supporting the successful implementation of novel hematology oncology therapies within community oncology practices. MILs provide evidence-based education and implementation support related to operational readiness, workflow integration, and care coordination, helping practices address real-world challenges and optimize delivery of innovative therapies.';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface QuestionType {
  id: string;
  label: string;
  placeholder: string;
}

export interface ProductBranding {
  primaryColor: string;
  secondaryColor: string;
}

export type IndicationParagraph = string | { intro: string; bullets: string[] };

export interface Product {
  id: string;
  brandName: string;
  genericName: string;
  drugClass: string;
  drugTarget?: string;
  branding: ProductBranding;
  websiteUrl: string;
  prescribingInfoUrl: string;
  disclaimer: string;
  indication: string;
  indicationLabel: 'Indication' | 'Indications';
  headerBullets?: string[];
  indicationAndUsage?: IndicationParagraph[];
}

export interface PharmaProductSupportTeam {
  id: string;
  name: string;
  specialty: string;
  manufacturer: string;
  expertId: number;
  cancerFocusId: number;
  indications: string[];
  indicationSummary: string;
  guidance: string;
  about: string[];
  supportedProducts: Product[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Question Types
// ─────────────────────────────────────────────────────────────────────────────

export const QUESTION_TYPES: QuestionType[] = [
  {
    id: 're-dosing',
    label: 'Dosing & Step-Up Protocols',
    placeholder: 'After a 3-week treatment delay, should I restart…',
  },
  {
    id: 'adverse-event',
    label: 'Adverse Event (AE) Management',
    placeholder: 'What are the recommended management strategies for…',
  },
  {
    id: 'restarting',
    label: 'Restarting or Retiming Therapy',
    placeholder: 'Can I resume therapy after…',
  },
  {
    id: 'academic',
    label: 'Guideline-Based Use',
    placeholder: 'Does NCCN recommend bispecifics before or after…',
  },
  {
    id: 'clinical-trials',
    label: 'Clinical Trials & Study Data',
    placeholder: 'What were the response rates…',
  },
  {
    id: 'packaging',
    label: 'Packaging or Label Clarification',
    placeholder: 'What are the vial sizes and storage requirements…',
  },
  {
    id: 'other',
    label: 'Other',
    placeholder: 'Write your question…',
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Pharma Companies & Products
// ─────────────────────────────────────────────────────────────────────────────

const PHARMA_TEAMS_DATA = [
  {
    id: 'jj-bispecifics',
    name: 'J&J Medical',
    manufacturer: 'Johnson & Johnson',
    specialty: 'Bispecific Antibodies',
    expertId: env.EXPO_PUBLIC_MOD_EXPERT_ID,
    cancerFocusId: 3,
    indications: [
      'Treatment of adult patients with relapsed or refractory multiple myeloma who have received at least four prior lines of therapy.',
    ],
    indicationSummary: 'Adult R/R multiple myeloma after ≥4 prior lines',
    guidance:
      'J&J Medical can answer product-specific scientific or logistical questions. Please be as specific as possible and review the guidelines.',
    about: [
      'Johnson & Johnson (J&J) Medical consists of clinically trained healthcare professionals who specialize in delivering comprehensive scientific information to support the use of J&J Innovative Medicine products across all therapeutic areas.',
      "The Medical Science Liaisons (MSLs) are a field-based group of Johnson & Johnson healthcare and scientific professionals, including PharmDs, PhDs, RNs, NPs, PAs, MDs, and DOs. MSLs provide scientific exchange tailored to your medical information request. MSLs serve as regional clinical resources to provide scientifically sound, objective, and current data and fair-balanced product information. MSLs present the scientific evidence supporting a product's use.",
      // Ordered to match the checkboxes.
      J_J_MEDICAL_ABOUT_MIL,
      'The J&J Medical Information Team engages with healthcare providers and patients to address inquiries on product-related topics including dosing and administration, adverse event management, clinical trial data, and other areas of patient care.',
      'Responses from J&J Medical are provided based on your unsolicited request and are not intended as an endorsement of any usage not contained in the prescribing information. While J&J Medical cannot provide individuals with treatment recommendations, their goal is to provide clinicians and patients with access to clinical and scientific information. For comprehensive product information, please refer to the full Prescribing Information. To report a possible adverse event or product quality complaint, please call the J&J Medical Information Team immediately, at 1-800-526-7736.',
    ],
    supportedProducts: [
      {
        id: 'tecvayli',
        brandName: 'TECVAYLI®',
        genericName: 'teclistamab-cqyv',
        drugClass: 'CD3 bispecific T-cell engager',
        drugTarget: 'BCMA',
        indication: 'Adult R/R multiple myeloma',
        indicationLabel: 'Indications',
        headerBullets: [
          'in combination with daratumumab and hyaluronidase-fihj after ≥1 prior line',
          'as monotherapy after ≥4 prior lines',
        ],
        disclaimer:
          'Please read full Prescribing Information, including Boxed WARNING, for TECVAYLI®.',
        branding: {
          primaryColor: '#503a91',
          secondaryColor: '#f3b651',
        },
        websiteUrl:
          'https://www.tecvayli.com/how-tecvayli-works/?gad_campaignid=23079079807&gbraid=0AAAAAo4Zhg4yvJu9jN2HBALzFRF6s2zHT',
        prescribingInfoUrl:
          'https://www.jnjlabels.com/package-insert/product-monograph/prescribing-information/TECVAYLI-pi.pdf',
        indicationAndUsage: [
          {
            intro:
              'TECVAYLI® (teclistamab-cqyv) is a bispecific B-cell maturation antigen (BCMA)-directed CD3 T-cell engager indicated for the treatment of adult patients with relapsed or refractory multiple myeloma:',
            bullets: [
              'in combination with daratumumab and hyaluronidase-fihj in patients who have received at least one prior line of therapy, including a proteasome inhibitor and an immunomodulatory agent.',
              'as monotherapy, in patients who have received at least four prior lines of therapy, including a proteasome inhibitor, an immunomodulatory agent and an anti-CD38 monoclonal antibody.',
            ],
          },
        ],
      },
      {
        id: 'talvey',
        brandName: 'TALVEY®',
        genericName: 'talquetamab-tgvs',
        drugClass: 'CD3 bispecific T-cell engager',
        drugTarget: 'GPRC5D',
        indication: 'Adult R/R multiple myeloma after ≥4 prior lines',
        indicationLabel: 'Indication',
        disclaimer:
          'Please read full Prescribing Information, including Boxed WARNING, for TALVEY®.',
        branding: {
          primaryColor: '#d64542',
          secondaryColor: '#f3b651',
        },
        websiteUrl: 'https://www.talveyhcp.com',
        prescribingInfoUrl:
          'https://www.jnjlabels.com/package-insert/product-monograph/prescribing-information/TALVEY-pi.pdf',
        indicationAndUsage: [
          'TALVEY® (talquetamab-tgvs) is indicated for the treatment of adult patients with relapsed or refractory multiple myeloma who have received at least four prior lines of therapy, including a proteasome inhibitor, an immunomodulatory agent and an anti-CD38 monoclonal antibody.',
          'This indication is approved under accelerated approval based on response rate and durability of response. Continued approval for this indication may be contingent upon verification and description of clinical benefit in a confirmatory trial(s).',
        ],
      },
    ],
  },
] as const satisfies readonly PharmaProductSupportTeam[];

export const PHARMA_TEAMS: PharmaProductSupportTeam[] = [...PHARMA_TEAMS_DATA];

export type ProductId = (typeof PHARMA_TEAMS_DATA)[number]['supportedProducts'][number]['id'];

export const PRODUCT_IDS = PHARMA_TEAMS_DATA.flatMap((team) =>
  team.supportedProducts.map((product) => product.id)
) as [ProductId, ...ProductId[]];

/** Multiple myeloma cancer focus used by the current MOD flow. */
export const MOD_CANCER_FOCUS_ID = '3';

/** Subtitle on the success card inviting the submitter to send a product-specific follow-up. */
export const PRODUCT_SUPPORT_FOLLOW_UP_DESCRIPTION =
  'Get product-specific follow-up from the J&J Medical Information Team. With a few edits, send your question and receive expert answers on dosing, adverse events, clinical data, and patient care.';

/** Brief team-scope blurb shown above the can-ask / avoid-asking guidelines. */
export const PRODUCT_SUPPORT_SCOPE_DESCRIPTION =
  'The J&J Medical Information Team can answer questions on product-related topics.';

/** Default prefill examples for Treatment Support success deep links. */
export const PRODUCT_SUPPORT_PREFILL_MESSAGES: Record<ProductId, string> = {
  talvey:
    'For a patient on talquetamab with grade 2 dysgeusia and ongoing weight loss, what dose modification and supportive care strategy do you recommend?',
  tecvayli:
    'For a patient starting teclistamab after anti-CD38 therapy, what step-up dosing and CRS monitoring plan do you recommend in community practice?',
};

// ─────────────────────────────────────────────────────────────────────────────
// Derived Data
// ─────────────────────────────────────────────────────────────────────────────

export const ALL_PRODUCTS: Product[] = PHARMA_TEAMS.flatMap((team) =>
  team.supportedProducts.map((product) => ({
    ...product,
  }))
);

const PRODUCT_MAP = new Map(ALL_PRODUCTS.map((p) => [p.id, p]));

// ─────────────────────────────────────────────────────────────────────────────
// Helper Functions
// ─────────────────────────────────────────────────────────────────────────────

export const getProductData = (productId: string): Product | undefined => {
  return PRODUCT_MAP.get(productId);
};

export const getProductSupportTeam = (teamId: string): PharmaProductSupportTeam | undefined => {
  return PHARMA_TEAMS.find((t) => t.expertId.toString() === teamId);
};

export const getSupportTeamByProductId = (
  productId: string
): PharmaProductSupportTeam | undefined => {
  return PHARMA_TEAMS.find((t) => t.supportedProducts.some((p) => p.id === productId));
};

export const getQuestionTypeLabel = (questionTypeId: string): string => {
  const questionType = QUESTION_TYPES.find((qt) => qt.id === questionTypeId);

  return questionType?.label || questionTypeId;
};

export const getQuestionTypePlaceholder = (questionTypeId: string): string => {
  const questionType = QUESTION_TYPES.find((qt) => qt.id === questionTypeId);

  return questionType?.placeholder || 'Ask your question, please be as specific as possible...';
};

// ─────────────────────────────────────────────────────────────────────────────
// Guidelines & Static Content
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// Routes (Direct-to-MSL)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Routing destinations, in display order.
 *
 * Order is most specific first. J&J may ask for MIL to lead instead, to
 * promote a new team.
 *
 * Descriptions are J&J's own role text, verbatim. Shortening one is a
 * conversation with them rather than an edit here, however long they run. The
 * MIL paragraph came from Vicki.
 */
export const MOD_ROUTES = [
  {
    id: 'msl',
    label: 'J&J MSL',
    description:
      'MSLs are available for engaging in clinical and scientific dialogue related to J&J Innovative Medicine products, disease states, and clinical practice trends.',
  },
  {
    id: 'mil',
    label: 'J&J MIL',
    description:
      'Medical Implementation Leads support the implementation of hematology therapies, including operational readiness, practice workflows, care coordination, and adoption barriers.',
  },
  {
    id: 'med_info',
    label: 'J&J Medical Information Team',
    description:
      'The Medical Information Team provides scientific information in response to questions regarding J&J Innovative Medicine products.',
  },
] as const satisfies readonly { id: string; label: string; description: string }[];

/**
 * The paragraph under the routing heading, shown only while the MIL flag is
 * off.
 *
 * Kept verbatim so turning the flag off restores exactly what is live today.
 * Delete it with the flag rather than carrying it forward: the first sentence
 * restates the heading, and the second promises auto-routing that nothing
 * implements.
 */
export const MOD_ROUTE_SECTION_HELPER_LEGACY =
  "Choose who should respond based on the type of support you need. Not sure? We'll route your question for you based on what you ask.";

/**
 * Content for the helper modal.
 *
 * Each row is a kind of question and the team that fields it. There are no
 * example questions: J&J's compliance group read quoted questions as
 * soliciting inquiries, so only the categories stay.
 *
 * The intro is Primum's, not J&J's: it is the one thing the table cannot say,
 * since a table of three rows reads as a choice of one.
 */
export const MOD_ROUTE_HELPER = {
  title: 'Not sure who to ask?',
  linkLabel: 'Not sure who to ask?',
  intro: 'You can send your question to more than one team.',
  rows: [
    {
      routeId: 'msl',
      asking: 'Clinical evidence, treatment sequencing, or scientific discussion',
    },
    {
      routeId: 'mil',
      asking: 'Putting treatment into practice',
    },
    {
      routeId: 'med_info',
      asking: 'Specific product information',
    },
  ],
} as const satisfies {
  title: string;
  linkLabel: string;
  intro: string;
  rows: readonly { routeId: string; asking: string }[];
};

export type ModRoute = (typeof MOD_ROUTES)[number]['id'];

/**
 * Routes assigned by territory, which are the ones that need a zip code.
 * Medical Information is a central team and needs none.
 *
 * Must match the routes the API requires a postal code for, or a submission
 * passes here and fails there with an error on a field the form never showed.
 */
export const TERRITORY_ROUTES: readonly ModRoute[] = ['msl', 'mil'];

export const MOD_ROUTE_IDS = MOD_ROUTES.map((route) => route.id) as [ModRoute, ...ModRoute[]];

export const PRODUCT_SUPPORT_GUIDELINES = {
  title: `${TREATMENT_SUPPORT_LABEL} Guidelines`,
  intro:
    'J&J Medical can answer questions within their approved scope. Please review example topics below before submitting your inquiry.',
  sections: [
    {
      heading: 'You can ask about',
      items: [
        'Dosing & Step-Up Protocols',
        'Adverse Event (AE) Management',
        'Restarting or Retiming Therapy',
        'Clinical Trials & Study Data',
        'Packaging or Label Clarification',
      ],
    },
    {
      heading: 'Please avoid asking about',
      items: [
        'Individual patient recommendations or treatment decisions',
        'Competitive therapies or comparative data',
        'NCCN or third-party guideline content not related to this product',
      ],
    },
  ],
};

/**
 * One row of the explainer's bullet grid: a matched pair drawing one contrast.
 *
 * `axis` is not rendered. It records *why* these two bullets share a row, so
 * whoever rewords one can tell whether the pairing survives. Break the axis and
 * the grid still renders while teaching the wrong rule.
 *
 * Keep both sides short. The bullets carry the decision on their own; a reader
 * who wants depth reads the examples below them.
 */
type ExplainerBulletRow = {
  axis: string;
  expert: string;
  team: string;
};

/**
 * One worked example: a real question and the side it belongs to.
 *
 * `destination` sorts the question into a column rather than labeling it. The
 * column heading is the answer, so no per-question destination label is
 * rendered and none can drift from the heading above it.
 *
 * Keep the two sides balanced. An odd count leaves one column longer than the
 * other and the divider runs past the short one.
 */
type ExplainerExample = {
  question: string;
  destination: 'expert' | 'team';
};

/**
 * Copy for the explainer opened from the Treatment Support card, telling a
 * clinician which side of the platform a question belongs to.
 *
 * Deliberately not the guidelines. `PRODUCT_SUPPORT_GUIDELINES` is CAC and J&J
 * approved and enumerates approved scope; this helps a user choose. Keep the
 * team-side bullets short so the two never converge.
 *
 * Two tiers on purpose: short paired bullets decide, examples are there for
 * whoever reads on. That is why the bullets do not try to enumerate every
 * question type — coverage the bullets drop belongs in an example.
 */
/**
 * How long the Treatment Support card carries its "new" marker.
 *
 * Two weeks, which is the launch email plus the following weekly email. Those
 * are the two things that actually bring people back, so the window is set by
 * touchpoints rather than by round numbers.
 *
 * Deliberately longer than the one week Fellows Corner used. That badge sits on
 * a nav item seen on every visit, so a short run was enough and a longer one
 * would have become wallpaper. This one sits on a directory card behind a
 * treatment-type filter, and the audience is intermittent by design: the
 * monthly-submitter target is 10 percent against roughly 6 to 7 percent actual,
 * so a seven-day window would close before most of the people it is for opened
 * the app at all.
 *
 * TEMPORARY: delete this and the badge with it once the date passes. A new
 * marker that outlives its claim trains people to stop reading badges.
 */
export const MOD_NEW_UNTIL = new Date('2026-09-17T00:00:00Z');

export const WHEN_TO_USE_EXPLAINER = {
  title: 'Not sure who to ask?',

  /**
   * The card's link to this modal.
   *
   * Card-scoped on purpose, and not the modal's own title, which it briefly
   * was. Matching link text to destination heading is normally the strongest
   * signal of where a link goes, but that holds only while the two share a
   * scope. Here they do not: the link sits inside the J&J card while the title
   * asks about a choice spanning that card and the experts beside it, so a
   * reader looking at one card met a link that was not about it. It also
   * presumed confusion, telling someone who knew what they wanted that they
   * might be lost.
   *
   * This is closer to how Vicki described the need, that nothing tells them
   * "when they should use it, like when is the right choice". The title stays
   * general, which is right once you are inside the comparison.
   *
   * Named rather than composed from the team name, so the card and the modal
   * can differ. Revisit if a second product-support team is added.
   */
  linkLabel: 'When to ask J&J Medical',

  /**
   * Announcement line. Takes the flag-resolved team name so the modal cannot
   * name a team differently from the card that opened it.
   */
  getIntro: (teamName: string) =>
    `You can now ask ${teamName} directly, alongside asking a Primum expert.`,

  /**
   * Headings, rows and examples below are Vicki's, pasted as text on
   * 2026-09-03 and used verbatim. Nothing here is paraphrased or invented.
   *
   * That matters more than usual on this surface. The modal exists to tell a
   * clinician which questions belong to a manufacturer, so an example nobody
   * approved is a clinical claim nobody approved. An earlier draft transcribed
   * from an image reached this file naming a real J&J study, RedirecTT-1, which
   * nobody at Primum could be shown to have written. Plausible enough to pass a
   * read, which is exactly what made it dangerous.
   *
   * The rule that came out of it: no clinical example reaches this modal unless
   * it came from her in text.
   */
  expertColumnHeading: 'Ask a Primum Expert',

  /**
   * Her wording, and deliberately not the flag-resolved team name the card
   * uses. She names the team fully here where the card says "J&J Medical",
   * which is the more precise name for what answers: the Medical Information
   * Team rather than J&J Medical at large.
   *
   * The cost is that the modal can name the team differently from the card that
   * opened it, which deriving it from the flag prevented. Left as she wrote it,
   * since which name is right is hers to decide.
   */
  /**
   * Shortened from "Ask J&J Medical Information" on 2026-09-03, at Yosevu's
   * instruction rather than from a new message from Vicki.
   *
   * The reason is consistency: this heading was the only place in the modal
   * using the fuller name, against the intro, the verdict lines, the primary
   * button and the card that opens it, all of which say "J&J Medical". A name
   * that changes once inside one small modal reads as two different teams.
   *
   * What it costs, so nobody has to rediscover it: the fuller name is the more
   * accurate description, a medical information team rather than clinicians,
   * and that is the distinction this whole modal exists to draw. If Vicki wrote
   * it deliberately for that reason, the fix is the other four places, not this
   * one. Worth confirming with her before launch.
   */
  teamColumnHeading: 'Ask J&J Medical',

  bulletRows: [
    {
      axis: 'this patient versus the protocol',
      expert: 'Patient-specific decisions',
      team: 'Dosing & step-up protocols',
    },
    {
      axis: 'choosing versus administering',
      expert: 'Treatment choice & sequencing',
      team: 'AE management & dose timing',
    },
    {
      axis: 'judgment versus documented data',
      expert: 'Clinical judgment in complex cases',
      team: 'Product label & study data',
    },
  ] as ExplainerBulletRow[],

  examplesHeading: 'For example',

  /**
   * Softer than the columns above, and hers. "Best place to start" invites a
   * first move where "ask X" reads as a rule, which matters because plenty of
   * questions could reasonably go either way.
   */
  /**
   * Column heading over the questions. Its pair is `exampleVerdictLabel`, which
   * doubles as the heading over the destinations, so the two never disagree.
   */
  exampleQuestionHeading: 'Question',

  exampleVerdictLabel: 'Best place to start',

  /**
   * Short names, as she wrote them in the verdict lines. Note she uses "J&J
   * Medical" here and the fuller "J&J Medical Information" in the column
   * heading. Kept as-is rather than reconciled, since the heading names the
   * destination once and the verdicts repeat it four times.
   */
  exampleDestinationNames: {
    expert: 'Primum Expert',
    team: 'J&J Medical',
  } as Record<ExplainerExample['destination'], string>,

  /**
   * Eyebrow for the examples tier when the columns are stacked.
   *
   * Side by side, the column heading above answers "for example, ask who?" and
   * a bare "For example" is enough. Stacked, both example groups sit below both
   * sets of topics, so an unqualified eyebrow appears twice with nothing
   * distinguishing the two. This carries the destination down with the
   * examples for that case only.
   */
  getStackedExamplesHeading: (columnHeading: string) =>
    `For example, ${columnHeading.toLowerCase()}`,

  /**
   * Grouped by destination, manufacturer pair then expert pair.
   *
   * The wide layout renders these in array order against a destination column,
   * and interleaved they made that column alternate down the table, which read
   * as four unrelated questions rather than two of each.
   *
   * The manufacturer leads, which is the order the stacked layout already uses:
   * the reader arrived from a J&J card by tapping a link about asking J&J, so
   * that is the half they came to read. It does mean the examples run opposite
   * to the comparison columns above them, where expert is still the left
   * column.
   */
  examples: [
    {
      question:
        "We're starting to administer bispecifics in our practice. What is the recommended step-up dosing schedule for Tecvayli?",
      destination: 'team',
    },
    {
      question: 'How common was CRS with talquetamab in clinical trials, and how severe was it?',
      destination: 'team',
    },
    {
      // "Tec-Dara" was the brand pairing here. Changed to the class, because
      // the question this example stands for is which modality comes next, and
      // naming a product made a sequencing question look like a product
      // question, which is the confusion the modal exists to resolve.
      //
      // Capitalised at Yosevu's instruction. Note that the manufacturer example
      // above says "bispecifics" lowercase, which is Vicki's own wording and so
      // was left alone; the two now differ.
      question:
        'My patient is progressing and is dara-naive. Should I consider Bispecifics before CAR-T?',
      destination: 'expert',
    },
    {
      question:
        '81-year-old man with high-risk MM, ECOG 2, treated with D-RVd in 1L and CAR-T in 2L, now with a rising M-spike. What would you consider next?',
      destination: 'expert',
    },
  ] as ExplainerExample[],

  /**
   * The expert side's way out of the modal. It closes rather than navigating,
   * since the modal opens from the directory and the expert list is already
   * behind it, but it is a labeled choice rather than a dismissal.
   */
  /**
   * The app's own name for this destination. `navigation.ts` labels the
   * `inquiry` route "Ask an Expert" in the web nav, and this button routes
   * there, so matching it means the modal is not inventing a second name for a
   * place the product already names.
   *
   * It also parallels the J&J label, "Ask {team}", which is what makes the two
   * read as a pair of choices rather than as an action and an escape. "Browse"
   * described a different thing from what the button does.
   */
  expertCtaLabel: 'Ask an Expert',

  getCtaLabel: (teamName: string) => `Ask ${teamName}`,
};
